import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link as ScrollLink, Element } from "react-scroll";
import AOS from "aos";
import "aos/dist/aos.css";

export default function Home() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      style={{
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      <style>
        {`
            @keyframes scrollTestimonials {
              0% {
                  transform: translateX(0%);
              }
              100% {
                  transform: translateX(-50%);
              }
            }
        `}
      </style>

      <nav style={{
        ...navStyle,
        backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
        boxShadow: isScrolled ? '0 2px 20px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        <div style={navContainerStyle}>
          <span style={navLogoStyle} onClick={() => navigate("/")} role="button">
            Dansy
          </span>
          
          <div style={navLinksStyle}>
            <ScrollLink to="features" smooth={true} duration={800} style={navLinkStyle}>
              Features
            </ScrollLink>
            <ScrollLink to="pricing" smooth={true} duration={800} style={navLinkStyle}>
              Pricing
            </ScrollLink>
            <ScrollLink to="testimonials" smooth={true} duration={800} style={navLinkStyle}>
              Testimonials
            </ScrollLink>
          </div>

          <div style={navButtonsStyle}>
            <button onClick={() => navigate("/login")} style={navSignInButton}>
              Sign In
            </button>
            <button onClick={() => navigate("/sessions")} style={navCTAButton}>
              Get Started Free
            </button>
          </div>
        </div>
      </nav>

      <section style={heroSectionStyle}>
        <div style={heroContentStyle}>
          <div style={heroBadgeStyle} data-aos="fade-down">
            ✨ The Future of Choreography Management
          </div>
          
          <h1 data-aos="fade-up" style={heroTitleStyle}>
            Create and optimize your choreographies with{" "}
            <span style={{ 
              fontFamily: "'Shirkhand', cursive",
              color: '#ff477e',
            }}>
              Dansy
            </span>
          </h1>

          <p data-aos="fade-up" data-aos-delay="100" style={heroSubtitleStyle}>
            The collaborative platform that helps dance teams create, manage, and perfect their choreographies. 
            Powered by AI optimization. Trusted by professional dancers worldwide.
          </p>

          <div style={heroButtonsContainerStyle} data-aos="fade-up" data-aos-delay="200">
            <button
              onClick={() => navigate("/sessions")}
              style={heroPrimaryButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 12px 24px rgba(255, 71, 126, 0.35)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 8px 16px rgba(255, 71, 126, 0.25)";
              }}
            >
              Start Free Trial
              <span style={{ marginLeft: '8px' }}>→</span>
            </button>
            
            <button
              style={{...heroSecondaryButtonStyle, opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#f0f0f0', color: '#999'}}
              disabled
            >
              Watch Demo
            </button>
          </div>

          <div style={heroStatsStyle} data-aos="fade-up" data-aos-delay="300">
            <div style={statItemStyle}>
              <div style={statNumberStyle}>-</div>
              <div style={statLabelStyle}>Active Users</div>
            </div>
            <div style={statDividerStyle}></div>
            <div style={statItemStyle}>
              <div style={statNumberStyle}>95%</div>
              <div style={statLabelStyle}>Time Saved</div>
            </div>
            <div style={statDividerStyle}></div>
            <div style={statItemStyle}>
              <div style={statNumberStyle}>AI</div>
              <div style={statLabelStyle}>Powered</div>
            </div>
          </div>
        </div>

        <div style={heroDecorativeCircle1}></div>
        <div style={heroDecorativeCircle2}></div>
      </section>

      <Element name="features">
        <section style={featuresSectionStyle}>
          <div style={sectionHeaderStyle}>
            <div style={sectionBadgeStyle} data-aos="fade-up">FEATURES</div>
            <h2 style={featuresTitleStyle} data-aos="fade-up" data-aos-delay="100">
              AI-powered optimization that saves you hours
            </h2>
            <p style={sectionSubtitleStyle} data-aos="fade-up" data-aos-delay="200">
              Let artificial intelligence handle the complex positioning while you focus on creativity
            </p>
          </div>
          
          <div style={featuresGridStyle}>
            <div data-aos="fade-up" data-aos-delay="100" style={{...featureCardStyle, border: '2px solid #ff477e', position: 'relative'}}>
              <div style={aiFeatureBadgeStyle}>⭐ AI POWERED</div>
              <div style={featureIconStyle}>🤖</div>
              <h3 style={{...featureCardTitleStyle, color: '#ff477e'}}>Smart Position Optimization</h3>
              <p style={featureCardTextStyle}>
                Our AI analyzes your choreography and automatically optimizes dancer positions to minimize transitions and maximize visual impact. Save hours of manual adjustments.
              </p>
            </div>
            
            <div data-aos="fade-up" data-aos-delay="200" style={featureCardStyle}>
              <div style={featureIconStyle}>🎵</div>
              <h3 style={featureCardTitleStyle}>Music Synchronization</h3>
              <p style={featureCardTextStyle}>
                Sync your movements perfectly with the music. Visual timeline for precise choreography planning.
              </p>
            </div>
            
            <div data-aos="fade-up" data-aos-delay="300" style={featureCardStyle}>
              <div style={featureIconStyle}>📐</div>
              <h3 style={featureCardTitleStyle}>Stage Positioning</h3>
              <p style={featureCardTextStyle}>
                Intuitive drag-and-drop interface to position dancers on stage. Create complex formations effortlessly.
              </p>
            </div>
            
            <div data-aos="fade-up" data-aos-delay="100" style={featureCardStyle}>
              <div style={featureIconStyle}>🎬</div>
              <h3 style={featureCardTitleStyle}>Timeline Editor</h3>
              <p style={featureCardTextStyle}>
                Plan every second of your choreography with our powerful timeline editor. Frame-by-frame precision.
              </p>
            </div>
            
            <div data-aos="fade-up" data-aos-delay="200" style={featureCardStyle}>
              <div style={featureIconStyle}>📊</div>
              <h3 style={featureCardTitleStyle}>Analytics & Insights</h3>
              <p style={featureCardTextStyle}>
                Track your progress and get insights on your choreographies. Optimize your creative process.
              </p>
            </div>
            
            <div data-aos="fade-up" data-aos-delay="300" style={{...featureCardStyle, position: 'relative'}}>
              <div style={featureIconStyle}>🎭</div>
              <h3 style={featureCardTitleStyle}>Real-time Collaboration</h3>
              <p style={featureCardTextStyle}>
                Work together with your team in real-time. See changes instantly and create together seamlessly.
              </p>
            </div>
          </div>
        </section>
      </Element>

      <Element name="pricing">
        <section style={pricingSectionStyle}>
          <div style={sectionHeaderStyle}>
            <div style={sectionBadgeStyle} data-aos="fade-up">PRICING</div>
            <h2 style={pricingTitleStyle} data-aos="fade-up" data-aos-delay="100">
              Choose the perfect plan for your team
            </h2>
            <p style={sectionSubtitleStyle} data-aos="fade-up" data-aos-delay="200">
              Start free, upgrade when you need more
            </p>
          </div>
          
          <div style={pricingGridStyle}>
            <div data-aos="fade-up" data-aos-delay="100" style={{...pricingCardStyle, opacity: 0.5, pointerEvents: 'none'}}>
              <div style={{...popularBadgeStyle, backgroundColor: '#999', color: '#fff'}}>NOT AVAILABLE</div>
              <div style={pricingHeaderStyle}>
                <h3 style={{...pricingPlanNameStyle, color: '#999'}}>Professional</h3>
                <div style={pricingPriceContainerStyle}>
                  <span style={{...pricingPriceStyle, color: '#999'}}>$19</span>
                  <span style={{...pricingPeriodStyle, color: '#999'}}>/month</span>
                </div>
                <p style={{...pricingDescriptionStyle, color: '#999'}}>For serious choreographers</p>
              </div>
              <ul style={pricingFeaturesListStyle}>
                <li style={pricingFeatureItemDisabledStyle}>— Unlimited projects</li>
                <li style={pricingFeatureItemDisabledStyle}>— Unlimited dancers</li>
                <li style={pricingFeatureItemDisabledStyle}>— All positioning tools</li>
                <li style={pricingFeatureItemDisabledStyle}>— 100GB storage</li>
                <li style={pricingFeatureItemDisabledStyle}>— Advanced analytics</li>
                <li style={pricingFeatureItemDisabledStyle}>— Priority support</li>
              </ul>
              <button 
                style={{...pricingButtonSecondaryStyle, cursor: 'not-allowed', backgroundColor: '#f0f0f0', color: '#999'}}
                disabled
              >
                Not Available
              </button>
            </div>
            
            <div data-aos="fade-up" data-aos-delay="200" style={{...pricingCardStyle, ...pricingCardPopularStyle}}>
              <div style={popularBadgeStyle}>AVAILABLE NOW</div>
              <div style={pricingHeaderStyle}>
                <h3 style={pricingPlanNameStyle}>Free</h3>
                <div style={pricingPriceContainerStyle}>
                  <span style={pricingPriceStyle}>$0</span>
                  <span style={pricingPeriodStyle}>/forever</span>
                </div>
                <p style={pricingDescriptionStyle}>Everything you need, for free</p>
              </div>
              <ul style={pricingFeaturesListStyle}>
                <li style={pricingFeatureItemStyle}>✓ Unlimited projects</li>
                <li style={pricingFeatureItemStyle}>✓ Unlimited dancers</li>
                <li style={pricingFeatureItemStyle}>✓ Unlimited periods</li>
                <li style={pricingFeatureItemStyle}>✓ AI Optimization</li>
                <li style={pricingFeatureItemStyle}>✓ Real-time collaboration</li>
                <li style={pricingFeatureItemStyle}>✓ All features included</li>
              </ul>
              <button 
                onClick={() => navigate("/sessions")}
                style={pricingButtonPrimaryStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 8px 16px rgba(255, 71, 126, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(255, 71, 126, 0.2)";
                }}
              >
                Get Started
              </button>
            </div>
            
            <div data-aos="fade-up" data-aos-delay="300" style={{...pricingCardStyle, opacity: 0.5, pointerEvents: 'none'}}>
              <div style={{...popularBadgeStyle, backgroundColor: '#999', color: '#fff'}}>NOT AVAILABLE</div>
              <div style={pricingHeaderStyle}>
                <h3 style={{...pricingPlanNameStyle, color: '#999'}}>Enterprise</h3>
                <div style={pricingPriceContainerStyle}>
                  <span style={{...pricingPriceStyle, color: '#999'}}>Custom</span>
                </div>
                <p style={{...pricingDescriptionStyle, color: '#999'}}>For large organizations</p>
              </div>
              <ul style={pricingFeaturesListStyle}>
                <li style={pricingFeatureItemDisabledStyle}>— Everything in Professional</li>
                <li style={pricingFeatureItemDisabledStyle}>— Dedicated account manager</li>
                <li style={pricingFeatureItemDisabledStyle}>— Custom integrations</li>
                <li style={pricingFeatureItemDisabledStyle}>— Unlimited storage</li>
                <li style={pricingFeatureItemDisabledStyle}>— 24/7 phone support</li>
                <li style={pricingFeatureItemDisabledStyle}>— SLA guarantee</li>
              </ul>
              <button 
                style={{...pricingButtonSecondaryStyle, cursor: 'not-allowed', backgroundColor: '#f0f0f0', color: '#999'}}
                disabled
              >
                Not Available
              </button>
            </div>
          </div>
        </section>
      </Element>

      <Element name="testimonials">
        <section style={testimonialsSectionStyle}>
          <div style={sectionHeaderStyle}>
            <div style={{...sectionBadgeStyle, backgroundColor: 'rgba(255,255,255,0.25)', color: 'white', border: '1px solid rgba(255,255,255,0.4)'}} data-aos="fade-up">TESTIMONIALS</div>
            <h2 style={testimonialsTitleStyle} data-aos="fade-up" data-aos-delay="100">
              Loved by dancers worldwide
            </h2>
            <p style={{...sectionSubtitleStyle, color: 'rgba(255,255,255,0.9)'}} data-aos="fade-up" data-aos-delay="200">
              Join thousands of happy choreographers
            </p>
          </div>
          
          <div style={testimonialsContainerStyle}>
            <div style={testimonialsSliderStyle}>
              {testimonials.concat(testimonials).map((t, index) => (
                <div key={index} style={{...testimonialCardStyle, position: 'relative'}}>
                  <div style={{position: 'absolute', top: '12px', right: '12px', fontSize: '10px', color: '#999', fontStyle: 'italic'}}>template testimony</div>
                  <div style={testimonialStarsStyle}>★★★★★</div>
                  <p style={testimonialTextStyle}>"{t.text}"</p>
                  <div style={testimonialAuthorContainerStyle}>
                    <div style={testimonialAvatarStyle}>{t.author.charAt(0)}</div>
                    <div>
                      <div style={testimonialAuthorStyle}>{t.author}</div>
                      <div style={testimonialRoleStyle}>{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </Element>

      <section style={ctaSectionStyle}>
        <div style={ctaContentStyle} data-aos="fade-up">
          <h2 style={ctaTitleStyle}>Ready to transform your choreography workflow?</h2>
          <p style={ctaSubtitleStyle}>
            Join thousands of dancers and choreographers who trust Dansy
          </p>
          <button 
            onClick={() => navigate("/sessions")}
            style={ctaButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 16px 32px rgba(255, 71, 126, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(255, 71, 126, 0.2)";
            }}
          >
            Start Your Free Trial Today →
          </button>
          <p style={ctaSmallTextStyle}>No credit card required • Free forever plan available</p>
        </div>
      </section>

      <footer style={footerStyle}>
        <div style={footerContentStyle}>
          <div style={footerColumnStyle}>
            <h3 style={footerLogoStyle}>Dansy</h3>
            <p style={footerDescriptionStyle}>
              The modern platform for choreography management. Create, collaborate, and perfect your dance routines.
            </p>
          </div>
          
          <div style={footerColumnStyle}>
            <h4 style={footerColumnTitleStyle}>Product</h4>
            <ul style={footerListStyle}>
              <li><ScrollLink to="features" smooth={true} duration={800} style={footerLinkStyle}>Features</ScrollLink></li>
              <li><ScrollLink to="pricing" smooth={true} duration={800} style={footerLinkStyle}>Pricing</ScrollLink></li>
              <li><a href="/sessions" style={footerLinkStyle}>Sessions</a></li>
            </ul>
          </div>
          
          <div style={footerColumnStyle}>
            <h4 style={footerColumnTitleStyle}>Company</h4>
            <ul style={footerListStyle}>
              <li><a href="/terms" style={footerLinkStyle}>Terms of Service</a></li>
              <li><a href="/privacy" style={footerLinkStyle}>Privacy Policy</a></li>
              <li><a href="mailto:contact@dansy.net" style={footerLinkStyle}>Contact</a></li>
            </ul>
          </div>
          
          <div style={footerColumnStyle}>
            <h4 style={footerColumnTitleStyle}>Connect</h4>
            <ul style={footerListStyle}>
              <li><a href="#" style={footerLinkStyle}>Twitter</a></li>
              <li><a href="#" style={footerLinkStyle}>Instagram</a></li>
              <li><a href="#" style={footerLinkStyle}>Facebook</a></li>
            </ul>
          </div>
        </div>
        
        <div style={footerBottomStyle}>
          <p>© {new Date().getFullYear()} Dansy. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

const testimonials = [
  { 
    text: "The AI optimization is absolutely mind-blowing! What used to take me hours now takes seconds. It finds positions I never would have thought of.", 
    author: "Sarah Johnson",
    role: "Professional Choreographer"
  },
  { 
    text: "I was skeptical about AI, but Dansy proved me wrong. The optimization algorithm understands dancer spacing and transitions better than I do!", 
    author: "Michael Chen",
    role: "Dance Studio Owner"
  },
  { 
    text: "Our competition team saves 10+ hours per routine thanks to the AI optimization. We focus on creativity while Dansy handles the technical details.", 
    author: "Emma Rodriguez",
    role: "Dance Team Director"
  },
  {
    text: "The instant optimization feature is magical. One click and my entire formation is perfectly balanced. This is the future of choreography.",
    author: "David Kim",
    role: "Contemporary Dance Company"
  },
  {
    text: "I love how easy it is to sync movements with music. Perfect for creating synchronized routines.",
    author: "Sophie Martin",
    role: "Ballet Instructor"
  },
  {
    text: "Dansy's timeline editor gives us frame-by-frame precision. Absolutely essential for our work.",
    author: "James Anderson",
    role: "Music Video Choreographer"
  },
];

const navStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  transition: 'all 0.3s ease',
  backdropFilter: 'blur(10px)',
};

const navContainerStyle = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '20px 40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const navLogoStyle = {
  fontSize: '1.5rem',
  fontFamily: "'Shirkhand', cursive",
  color: '#ff477e',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
};

const navLinksStyle = {
  display: 'flex',
  gap: '32px',
  alignItems: 'center',
};

const navLinkStyle = {
  color: '#333',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: '0.95rem',
  cursor: 'pointer',
  transition: 'color 0.2s ease',
};

const navButtonsStyle = {
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
};

const navSignInButton = {
  padding: '10px 24px',
  fontSize: '0.95rem',
  backgroundColor: 'transparent',
  color: '#333',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 500,
  transition: 'background-color 0.2s ease',
};

const navCTAButton = {
  padding: '10px 24px',
  fontSize: '0.95rem',
  backgroundColor: '#ff477e',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 8px rgba(255, 71, 126, 0.2)',
};

const heroSectionStyle = {
  position: "relative",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  background: "linear-gradient(135deg, #fff5f7 0%, #ffe8f0 100%)",
  color: "#333",
  textAlign: "center",
  overflow: "hidden",
  padding: "140px 40px 80px",
};

const heroContentStyle = {
  textAlign: "center",
  maxWidth: "900px",
  zIndex: 2,
};

const heroBadgeStyle = {
  display: 'inline-block',
  padding: '8px 20px',
  backgroundColor: 'rgba(255, 71, 126, 0.1)',
  color: '#ff477e',
  borderRadius: '50px',
  fontSize: '0.9rem',
  fontWeight: 600,
  marginBottom: '24px',
  border: '1px solid rgba(255, 71, 126, 0.2)',
};

const heroTitleStyle = {
  fontSize: "4rem",
  marginBottom: "1.5rem",
  fontWeight: 700,
  lineHeight: 1.2,
};

const heroSubtitleStyle = {
  fontSize: "1.25rem",
  marginBottom: "2.5rem",
  lineHeight: 1.6,
  color: '#666',
  maxWidth: '700px',
  margin: '0 auto 2.5rem',
};

const heroButtonsContainerStyle = {
  display: 'flex',
  gap: '16px',
  justifyContent: 'center',
  marginBottom: '3rem',
};

const heroPrimaryButtonStyle = {
  padding: "16px 40px",
  fontSize: "1.1rem",
  backgroundColor: "#ff477e",
  color: "#fff",
  border: "none",
  borderRadius: "12px",
  cursor: "pointer",
  boxShadow: "0 8px 16px rgba(255, 71, 126, 0.25)",
  transition: "all 0.3s ease",
  fontWeight: 600,
};

const heroSecondaryButtonStyle = {
  padding: "16px 40px",
  fontSize: "1.1rem",
  backgroundColor: "white",
  color: "#333",
  border: "2px solid #e0e0e0",
  borderRadius: "12px",
  cursor: "pointer",
  transition: "all 0.3s ease",
  fontWeight: 600,
};

const heroStatsStyle = {
  display: 'flex',
  gap: '48px',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: '3rem',
};

const statItemStyle = {
  textAlign: 'center',
};

const statNumberStyle = {
  fontSize: '2rem',
  fontWeight: 700,
  color: '#ff477e',
  marginBottom: '4px',
};

const statLabelStyle = {
  fontSize: '0.9rem',
  color: '#666',
};

const statDividerStyle = {
  width: '1px',
  height: '50px',
  backgroundColor: '#e0e0e0',
};

const heroDecorativeCircle1 = {
  position: 'absolute',
  width: '500px',
  height: '500px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(255,71,126,0.1) 0%, rgba(255,71,126,0) 70%)',
  top: '-250px',
  right: '-250px',
  zIndex: 1,
};

const heroDecorativeCircle2 = {
  position: 'absolute',
  width: '400px',
  height: '400px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(255,107,157,0.1) 0%, rgba(255,107,157,0) 70%)',
  bottom: '-200px',
  left: '-200px',
  zIndex: 1,
};

const sectionHeaderStyle = {
  textAlign: 'center',
  marginBottom: '4rem',
};

const sectionBadgeStyle = {
  display: 'inline-block',
  padding: '6px 16px',
  backgroundColor: 'rgba(255, 71, 126, 0.1)',
  color: '#ff477e',
  borderRadius: '50px',
  fontSize: '0.85rem',
  fontWeight: 700,
  marginBottom: '16px',
  letterSpacing: '1px',
};

const sectionSubtitleStyle = {
  fontSize: '1.1rem',
  color: '#666',
  maxWidth: '600px',
  margin: '0 auto',
};

const featuresSectionStyle = {
  padding: "100px 40px",
  backgroundColor: "#fff",
  maxWidth: '1200px',
  margin: '0 auto',
};

const featuresTitleStyle = {
  fontSize: "2.5rem",
  marginBottom: "1rem",
  color: "#333",
  fontWeight: 700,
};

const featuresGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: "32px",
  marginTop: "20px",
};

const featureCardStyle = {
  backgroundColor: "#fff",
  padding: "40px 32px",
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
  transition: "all 0.3s ease",
  cursor: "pointer",
  border: '1px solid #f0f0f0',
};

const featureIconStyle = {
  fontSize: '3rem',
  marginBottom: '20px',
};

const featureCardTitleStyle = {
  fontSize: "1.5rem",
  marginBottom: "12px",
  color: "#333",
  fontWeight: 600,
};

const featureCardTextStyle = {
  fontSize: "1rem",
  color: "#666",
  lineHeight: 1.6,
};

const pricingSectionStyle = {
  padding: "100px 40px",
  backgroundColor: "#fafafa",
  maxWidth: '1200px',
  margin: '0 auto',
};

const comingSoonBadgeStyle = {
  position: 'absolute',
  top: '16px',
  right: '16px',
  fontSize: '0.65rem',
  fontWeight: '600',
  color: '#ff477e',
  backgroundColor: 'rgba(255, 71, 126, 0.1)',
  padding: '4px 10px',
  borderRadius: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  border: '1px solid rgba(255, 71, 126, 0.2)',
};

const aiFeatureBadgeStyle = {
  position: 'absolute',
  top: '16px',
  right: '16px',
  fontSize: '0.65rem',
  fontWeight: '700',
  color: 'white',
  background: 'linear-gradient(135deg, #ff477e, #ff6b9d)',
  padding: '6px 12px',
  borderRadius: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  boxShadow: '0 4px 12px rgba(255, 71, 126, 0.3)',
};

const pricingTitleStyle = {
  fontSize: "2.5rem",
  marginBottom: "1rem",
  color: "#333",
  fontWeight: 700,
};

const pricingGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: "32px",
  marginTop: "40px",
};

const pricingCardStyle = {
  backgroundColor: "#fff",
  padding: "40px 32px",
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
  transition: "all 0.3s ease",
  border: '2px solid #f0f0f0',
  position: 'relative',
};

const pricingCardPopularStyle = {
  border: '2px solid #ff477e',
  transform: 'scale(1.05)',
  boxShadow: "0 8px 32px rgba(255, 71, 126, 0.15)",
};

const popularBadgeStyle = {
  position: 'absolute',
  top: '-12px',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: '#ff477e',
  color: 'white',
  padding: '6px 20px',
  borderRadius: '50px',
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '1px',
};

const pricingHeaderStyle = {
  textAlign: 'center',
  marginBottom: '32px',
  paddingBottom: '32px',
  borderBottom: '1px solid #f0f0f0',
};

const pricingPlanNameStyle = {
  fontSize: '1.5rem',
  fontWeight: 700,
  color: '#333',
  marginBottom: '16px',
};

const pricingPriceContainerStyle = {
  marginBottom: '12px',
};

const pricingPriceStyle = {
  fontSize: '3rem',
  fontWeight: 700,
  color: '#ff477e',
};

const pricingPeriodStyle = {
  fontSize: '1.1rem',
  color: '#666',
  marginLeft: '8px',
};

const pricingDescriptionStyle = {
  fontSize: '1rem',
  color: '#666',
};

const pricingFeaturesListStyle = {
  listStyle: 'none',
  padding: 0,
  margin: '0 0 32px 0',
};

const pricingFeatureItemStyle = {
  padding: '12px 0',
  color: '#333',
  fontSize: '0.95rem',
};

const pricingFeatureItemDisabledStyle = {
  ...pricingFeatureItemStyle,
  color: '#999',
};

const pricingButtonPrimaryStyle = {
  width: '100%',
  padding: '14px 32px',
  fontSize: '1rem',
  backgroundColor: '#ff477e',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(255, 71, 126, 0.2)',
};

const pricingButtonSecondaryStyle = {
  width: '100%',
  padding: '14px 32px',
  fontSize: '1rem',
  backgroundColor: 'white',
  color: '#333',
  border: '2px solid #e0e0e0',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 600,
  transition: 'all 0.3s ease',
};

const testimonialsSectionStyle = {
  padding: "100px 0",
  background: "linear-gradient(135deg, #ff477e 0%, #ff6b9d 100%)",
  textAlign: "center",
  color: "#fff",
  overflow: "hidden",
};

const testimonialsTitleStyle = {
  fontSize: "2.5rem",
  marginBottom: "1rem",
  fontWeight: 700,
  color: 'white',
  textShadow: '0 2px 20px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.3)',
};

const testimonialsContainerStyle = {
  overflow: "hidden",
  minHeight: "320px",
  position: "relative",
  marginTop: '4rem',
};

const testimonialsSliderStyle = {
  display: "inline-flex",
  animation: "scrollTestimonials 40s linear infinite",
  gap: '24px',
};

const testimonialCardStyle = {
  flex: "0 0 400px",
  backgroundColor: "#fff",
  color: "#333",
  padding: "40px 32px",
  margin: "0 12px",
  borderRadius: "16px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
  textAlign: "left",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  minHeight: '280px',
};

const testimonialTextStyle = {
  fontSize: "1.05rem",
  lineHeight: 1.6,
  marginBottom: "24px",
  color: '#333',
  flexGrow: 1,
};

const testimonialStarsStyle = {
  fontSize: "1.2rem",
  color: "#ff477e",
  marginBottom: "16px",
};

const testimonialAuthorContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

const testimonialAvatarStyle = {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  backgroundColor: '#ff477e',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.2rem',
  fontWeight: 700,
};

const testimonialAuthorStyle = {
  fontWeight: 700,
  fontSize: "1rem",
  color: '#333',
};

const testimonialRoleStyle = {
  fontSize: '0.85rem',
  color: '#666',
  marginTop: '2px',
};

const ctaSectionStyle = {
  padding: "100px 40px",
  backgroundColor: "#fff",
  textAlign: "center",
};

const ctaContentStyle = {
  maxWidth: "700px",
  margin: "0 auto",
};

const ctaTitleStyle = {
  fontSize: "2.5rem",
  fontWeight: 700,
  marginBottom: "1.5rem",
  color: "#333",
};

const ctaSubtitleStyle = {
  fontSize: "1.25rem",
  color: "#666",
  marginBottom: "2.5rem",
  lineHeight: 1.6,
};

const ctaButtonStyle = {
  padding: "18px 48px",
  fontSize: "1.1rem",
  backgroundColor: "#ff477e",
  color: "#fff",
  border: "none",
  borderRadius: "12px",
  cursor: "pointer",
  boxShadow: "0 8px 24px rgba(255, 71, 126, 0.2)",
  transition: "all 0.3s ease",
  fontWeight: 600,
  marginBottom: '1.5rem',
};

const ctaSmallTextStyle = {
  fontSize: '0.9rem',
  color: '#999',
};

const footerStyle = {
  padding: "80px 40px 40px",
  backgroundColor: "#1a1a1a",
  color: "white",
};

const footerContentStyle = {
  maxWidth: "1200px",
  margin: "0 auto",
  display: "grid",
  gridTemplateColumns: "2fr 1fr 1fr 1fr",
  gap: "48px",
  marginBottom: "48px",
};

const footerColumnStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
};

const footerLogoStyle = {
  fontSize: "1.8rem",
  fontFamily: "'Shirkhand', cursive",
  color: "#ff477e",
  marginBottom: "12px",
};

const footerDescriptionStyle = {
  fontSize: "0.95rem",
  color: "#999",
  lineHeight: 1.6,
};

const footerColumnTitleStyle = {
  fontSize: "1rem",
  fontWeight: 600,
  marginBottom: "8px",
  color: "white",
};

const footerListStyle = {
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const footerLinkStyle = {
  color: "#999",
  textDecoration: "none",
  fontSize: "0.9rem",
  transition: "color 0.2s ease",
  cursor: 'pointer',
};

const footerBottomStyle = {
  maxWidth: "1200px",
  margin: "0 auto",
  paddingTop: "32px",
  borderTop: "1px solid #333",
  textAlign: "center",
  color: "#666",
  fontSize: "0.9rem",
};
